import { useCallback, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import dayjsBusinessTime from 'dayjs-business-time';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ORDER_METHODS } from '../../../services/exports/Constants';
import { CompanyDetailsInterface } from '../../../services/exports/Interfaces';
import { useGetHolidaysQuery } from '../../../store/api-slices';
import { useSelector } from 'react-redux';
import { selectCompany } from '../../../store/slices/InitialDataSlice';
import { buildBusinessTimes, resolveUserTimezone } from './utils';

dayjs.extend(customParseFormat);
dayjs.extend(dayjsBusinessTime);
dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
  orderMethod: ORDER_METHODS;
  company?: CompanyDetailsInterface;
  fromDate?: string;
  withTimeOffsets?: boolean;
  skipHolidays?: boolean;
}

const useCompanyBusinessTime = (props: Props) => {
  const isDelivery = props?.orderMethod === ORDER_METHODS.DELIVERY;
  const defaultCompany = useSelector(selectCompany);
  const company = props?.company || defaultCompany;
  const workSchedule = isDelivery ? company?.delivery_schedule : company?.work_schedule;
  const lastOrderGapPickup = company?.last_order_gap_pickup ?? 0;
  const lastOrderGapDelivery = company?.last_order_gap_delivery ?? 0;
  const lastOrderGap = props.orderMethod === ORDER_METHODS.DELIVERY ? lastOrderGapDelivery : lastOrderGapPickup;
  const minScheduleAheadDelivery = company?.min_schedule_ahead_time_delivery ?? 0;
  const minScheduleAheadPickup = company?.min_schedule_ahead_time_pickup ?? 0;
  const scheduleAheadTime =
    props.orderMethod === ORDER_METHODS.DELIVERY ? minScheduleAheadDelivery : minScheduleAheadPickup;

  const companyTz = workSchedule?.[0]?.timezone ?? company?.timezone ?? 'Europe/Berlin';
  const userTz = resolveUserTimezone(companyTz);

  const now = dayjs();
  const normalizedStartDate = props.fromDate ?? now.format('YYYY-MM');
  const isWithinMaxPreOrderDays = (date: Dayjs): boolean => {
    if (!date) {
      return true;
    }

    const maxPreOderDate = now.clone().add(maxPreOrderDays, 'days');

    return date.isSame(maxPreOderDate, 'days') || date.isBefore(maxPreOderDate, 'days');
  };

  const maxPreOrderDays = company?.max_pre_order_days ?? 0;
  const endDate = now.clone().add(maxPreOrderDays, 'days').format('YYYY-MM');

  const skipGetHolidaysQuery = !company?.id || props.skipHolidays;
  const { data: holidays, isLoading: isHolidaysLoading } = useGetHolidaysQuery(
    {
      companyId: company?.id,
      params: { order_method: props.orderMethod, start_date: normalizedStartDate, end_date: endDate },
    },
    { skip: skipGetHolidaysQuery, refetchOnFocus: true, refetchOnMountOrArgChange: 60 },
  );

  const businessTimes = useMemo(() => {
    if (!workSchedule?.length) {
      return null;
    }

    return buildBusinessTimes({
      workSchedule,
      companyTz,
      userTz,
      scheduleAheadTime,
      lastOrderGap,
      withTimeOffsets: props.withTimeOffsets,
    });
  }, [workSchedule, companyTz, userTz, props.withTimeOffsets, scheduleAheadTime, lastOrderGap]);

  const withCompanyBusinessTime = useCallback(
    <T>(fn: (d: typeof dayjs) => T): T => {
      if (!businessTimes) {
        return;
      }

      dayjs.setBusinessTime(businessTimes);
      dayjs.setHolidays(holidays ?? []);

      return fn(dayjs);
    },
    [businessTimes, holidays],
  );
  const isHoliday = (date: string): boolean => withCompanyBusinessTime(() => dayjs(date).isHoliday());
  const isBusinessTime = (date: string): boolean => {
    if (!isWithinMaxPreOrderDays(dayjs(date))) {
      return;
    }

    return withCompanyBusinessTime(() => dayjs(date).isBusinessTime());
  };

  const isBusinessDay = (date: string): boolean => {
    const normalizedDate = normalizeDate(date);

    if (!isWithinMaxPreOrderDays(dayjs(normalizedDate))) {
      return;
    }

    return withCompanyBusinessTime(() => {
      if (!dayjs(normalizedDate).isBusinessDay()) {
        return;
      }

      if (dayjs(normalizedDate).isBusinessTime()) {
        return true;
      }

      return dayjs(normalizedDate).nextBusinessTime().isSame(normalizedDate, 'day');
    });
  };

  const normalizeDate = (date: string) => {
    const dayjsDate = dayjs(date);
    const isToday = date && dayjsDate.isSame(now, 'day');

    if (!date || isToday || !isWithinMaxPreOrderDays(dayjsDate)) {
      return now;
    }

    return dayjsDate.startOf('day');
  };

  const getNextBusinessTime = (date: string): string => {
    if (!isWithinMaxPreOrderDays(dayjs(date))) {
      const normalizedDate = normalizeDate(date);

      return withCompanyBusinessTime(() => dayjs(normalizedDate).nextBusinessTime().toISOString());
    }

    return withCompanyBusinessTime(() => dayjs(date).nextBusinessTime().toISOString());
  };

  const hasOpeningDays = useMemo(() => {
    if (isHolidaysLoading || !businessTimes) {
      return false;
    }

    // Opening days repeat weekly, so scan at least a full week (and the whole pre-order window if longer),
    // capped to a year so a pathological max_pre_order_days can't blow up the loop. The scheduled/pre-order
    // option must appear whenever the restaurant opens on ANY upcoming day — independent of whether TODAY is
    // open or already past its closing time. We read the weekday's business hours directly (not isBusinessDay,
    // which is biased to "open right now" and can otherwise recurse over nextBusinessDay). `businessTimes`
    // keys are English weekday names, so the lookup is locale-pinned to avoid a non-English global dayjs locale.
    const daysToScan = Math.min(Math.max(maxPreOrderDays, 6), 366);

    for (let i = 0; i <= daysToScan; i++) {
      const day = now.clone().add(i, 'days');
      const intervals = businessTimes[day.locale('en').format('dddd').toLowerCase()];

      if (intervals?.length && !isHoliday(day.toISOString())) {
        return true;
      }
    }

    return false;
  }, [isHolidaysLoading, businessTimes, maxPreOrderDays, isHoliday]);
  const isOpenWholeDay = (date: string): boolean => {
    if (!businessTimes) {
      return;
    }

    const businessDay = dayjs(date);
    const dayKey = businessDay.format('dddd').toLowerCase();
    const intervals = businessTimes[dayKey] || [];

    if (!intervals.length) {
      return;
    }

    const END = 24 * 60 * 60;

    let workingTimeSecs = 0;

    for (const { start, end } of intervals) {
      const open = dayjs(start, ['HH:mm', 'HH:mm:ss']).set('second', 0);
      let close = dayjs(end, ['HH:mm', 'HH:mm:ss']);

      if (close.minute() === 59) {
        close = close.set('second', 0).add(1, 'minute');
      }

      workingTimeSecs += close.diff(open, 'second');
    }

    return workingTimeSecs === END;
  };

  return {
    isLoading: props.skipHolidays ? false : isHolidaysLoading,
    isWithinMaxPreOrderDays,
    businessTimes,
    holidays,
    getNextBusinessTime,
    isBusinessDay,
    isBusinessTime,
    hasOpeningDays,
    maxPreOrderDays,
    isHoliday,
    isOpenWholeDay,
  };
};

export default useCompanyBusinessTime;
